import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SHA256 from "crypto-js/sha256";
import CyberSourceCheckout from "../components/CyberSourceCheckout";
import { FaClock, FaCreditCard, FaShieldAlt, FaTag } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const SECRET_KEY = "D1UyXAdv1eBw8mnP";
// const PG_REDIRECT_URL = 'https://pg-api.qpay.gov.qa/qcb-pg/api/gateway/2.0';
const PG_REDIRECT_URL = "https://pguat.qcb.gov.qa/qcb-pg/api/gateway/2.0";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function nowDDMMYYYYhhmmss() {
  const d = new Date();
  return (
    pad2(d.getDate()) +
    pad2(d.getMonth() + 1) +
    d.getFullYear() +
    pad2(d.getHours()) +
    pad2(d.getMinutes()) +
    pad2(d.getSeconds())
  );
}

export default function PaymentGateway() {
    const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [subscription, setSubscription] = useState(null);

  // Handle redirect back from QPay
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    console.log("Search params:", search.toString());

    if (search.toString().length > 0) {
      // This is a callback from payment gateway
      setLoading(true);
      const params = Object.fromEntries(search.entries());
      console.log("Callback params:", params);

      const recvHash = params["Response.SecureHash"];
      console.log("Received hash:", recvHash);

      if (!recvHash) {
        setError("No secure hash in callback");
        setLoading(false);
        return;
      }

      // Remove SecureHash from params for validation
      const paramsForHash = { ...params };
      delete paramsForHash["Response.SecureHash"];

      // Build hash string according to the documentation
      // Parameters must be ordered alphabetically by parameter name
      const sortedKeys = Object.keys(paramsForHash).sort();
      let hashString = SECRET_KEY;

      // Process parameters in alphabetical order
      sortedKeys.forEach((key) => {
        let value = paramsForHash[key] || "";

        // Handle StatusMessage - replace spaces with +
        if (key === "Response.StatusMessage") {
          value = value.replace(/ /g, "+");
        }

        hashString += value;
      });

      console.log("Hash string for validation:", hashString);
      const calculatedHash = SHA256(hashString).toString();
      console.log("Calculated hash:", calculatedHash);

      if (calculatedHash !== recvHash) {
        console.error(
          "Hash mismatch - Expected:",
          recvHash,
          "Calculated:",
          calculatedHash
        );
        setError("Secure hash mismatch - payment verification failed");
        setLoading(false);
        return;
      }

      // Hash is valid, process the response
      const status = params["Response.Status"];
      if (status === "0000") {
        navigate("/payment-success", { state: params });
      } else {
        navigate("/payment-failed", { state: params });
      }
    } else {
      // No callback parameters, check for subscription data
      if (location.state && location.state.subscription) {
        console.log(
          "Payment Gateway subscription",
          location?.state?.subscription
        );
        setSubscription(location.state?.subscription);
      } else {
        // Try to get subscription from localStorage or other source
        // as a fallback when coming back from payment gateway
        const storedSubscription = localStorage.getItem("pendingSubscription");
        console.log(
          "Payment Gateway storedSubscription",
          storedSubscription
        );
        if (storedSubscription) {
          try {
            setSubscription(JSON.parse(storedSubscription));
          } catch (e) {
            console.error("Error parsing stored subscription:", e);
            setError("Missing subscription data");
          }
        } else {
          setError("Missing subscription data");
        }
      }
    }
  }, [location, navigate]);

  const initiatePayment = (e) => {
    e.preventDefault();
    if (!subscription) {
      setError("No subscription to process.");
      return;
    }
    setLoading(true);
    setError("");

    // Store subscription data before redirect (as fallback)
    localStorage.setItem("pendingSubscription", JSON.stringify(subscription));
    localStorage.setItem("mySubscription", JSON.stringify(subscription));

    const PUN = "txn_" + Date.now();
    const sessionId = `${subscription.subscription_id}-${
      subscription.client_id
    }-${subscription.client_subscription_id}-${Date.now()}`;
    const trxDate = nowDDMMYYYYhhmmss();
    // Calculate total amount after discount
    let totalAmount = subscription.subscription_cost;

    if (subscription?.coupon) {
      const discount = subscription.coupon.discount_value;
      if (subscription.coupon.discount_type === "percentage") {
        totalAmount = subscription.subscription_cost * (1 - discount / 100);
      } else {
        totalAmount = subscription.subscription_cost - discount;
      }
    }

    // Ensure it's not negative
    totalAmount = Math.max(0, totalAmount);
    const data = {
      Action: "0",
      Amount: Math.round(totalAmount * 100),
      BankID: "QPAYPG01",
      CurrencyCode: "634",
      ExtraFields_f14: window.location.origin + "/payment",
      Lang: "en",
      MerchantID: "1000250901",
      MerchantModuleSessionID: sessionId,
      PUN,
      PaymentDescription: subscription.subscription_name,
      Quantity: "1",
      TransactionRequestDate: trxDate,
    };

    const orderedKeys = Object.keys(data).sort();
    const toHash = SECRET_KEY + orderedKeys.map((k) => data[k]).join("");
    const secureHash = SHA256(toHash).toString();

    console.log("Payment initiation hash string:", toHash);
    console.log("Payment initiation secure hash:", secureHash);

    const formEl = document.createElement("form");
    formEl.method = "POST";
    formEl.action = PG_REDIRECT_URL;

    orderedKeys.forEach((key) => {
      const inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = key;
      inp.value = data[key];
      formEl.appendChild(inp);
    });

    const hashInput = document.createElement("input");
    hashInput.type = "hidden";
    hashInput.name = "SecureHash";
    hashInput.value = secureHash;
    formEl.appendChild(hashInput);

    document.body.appendChild(formEl);
    formEl.submit();
  };

  // Clean up stored subscription on component unmount
  useEffect(() => {
    return () => {
      // Clean up stored subscription when leaving the page successfully
      if (location.pathname !== "/payment") {
        localStorage.removeItem("pendingSubscription");
      }
    };
  }, [location.pathname]);
  const calculateTotal = () => {
    if (!subscription?.coupon) return subscription.subscription_cost;

    if (subscription.coupon.discount_type === "percentage") {
      return (
        subscription.subscription_cost *
        (1 - subscription.coupon.discount_value / 100)
      ).toFixed(2);
    } else {
      return (
        subscription.subscription_cost - subscription.coupon.discount_value
      ).toFixed(2);
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">{t("CompleteYourOrder")}</h1>
            <div className="flex items-center space-x-2 text-blue-100">
              <FaShieldAlt />
              <span className="text-sm">{t("SecureCheckout")}</span>
            </div>
          </div>
        </div>
        {error && (
          <div className="mb-4 bg-red-100 text-red-700 p-3 rounded text-sm">
            {error}
          </div>
        )}
        <div className="p-8">
   
          {subscription ? (
            <div className="w-full grid md:grid-cols-2 gap-8">
              {/* Order Summary */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">1</span>
                  </div>
                  {t("OrderSummary")}
                </h2>

                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {subscription.subscription_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {t("ordernumber")} : 
                        {subscription.subscription_id +
                          "-" +
                          subscription.client_id +
                          "-" +
                          subscription.client_subscription_id +
                          "-" +
                          Date.now()}
                      </p>
                    </div>
                    <span className="font-semibold">
                      {subscription.subscription_cost} QAR
                    </span>
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <FaClock className="mr-2" />
                    <span>{subscription.duration_days} {t("daysduration")}</span>
                  </div>
                </div>

                {subscription?.coupon && (
                  <>
                    <div className="border-t pt-3">
                      <div className="flex justify-between text-sm">
                        <span className="flex items-center text-green-600">
                          <FaTag className="mr-2" />
                          {subscription?.coupon.discount_type === "percentage"
                            ? `${subscription?.coupon.discount_value}%`
                            : `QAR ${subscription?.coupon.discount_value}`}
                        </span>
                        <span className="text-green-600">
                          {subscription?.coupon.discount_type === "percentage"
                            ? (
                                subscription.subscription_cost *
                                (1 - subscription.coupon.discount_value / 100)
                              ).toFixed(2)
                            : (
                                subscription.subscription_cost -
                                subscription.coupon.discount_value
                              ).toFixed(2)}
                          QAR
                        </span>
                      </div>
                    </div>
                  </>
                )}
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{t("total")}</span>
                    <span className="text-xl font-bold text-blue-600">
                      {calculateTotal()}
                      QAR
                    </span>
                  </div>
                </div>
              </div>
              {/* Payment Section */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">2</span>
                  </div>
                 {t("PaymentDetails")}
                </h2>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center mb-4">
                    <FaCreditCard className="text-blue-600 mr-3" />
                    <span className="font-medium">{t("SecurePayment")}</span>
                  </div>
                  {/* CyberSourceCheckout would go here */}
                  <div className="bg-white rounded border-2 border-dashed border-blue-200 p-6 text-center">
                    <CyberSourceCheckout subscription={subscription} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-600 text-sm mb-4">
              Loading subscription details...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
// {/* <form className="mb-2" onSubmit={initiatePayment}>
//                 <button
//                   type="submit"
//                   disabled={loading || !subscription}
//                   className="flex items-center justify-center w-full  gap-2 py-2 px-4 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
//                 >
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     viewBox="18.4466 -44.2328 512 332.8"
//                     className="w-8 h-8"
//                   >
//                     <g
//                       id="_x36_"
//                       transform="matrix(1, 0, 0, 1, 5.684341886080802e-14, 0)"
//                     >
//                       <g
//                         id="SVGRepo_bgCarrier"
//                         strokeWidth="0"
//                         transform="matrix(1, 0, 0, 1, -84.029373, -13.642785)"
//                       />
//                       <g
//                         id="SVGRepo_tracerCarrier"
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         transform="matrix(1, 0, 0, 1, -84.029373, -13.642785)"
//                       />
//                       <g
//                         id="SVGRepo_iconCarrier"
//                         transform="matrix(1, 0, 0, 1, -84.029373, -13.642785)"
//                       >
//                         <g>
//                           <g>
//                             <g />
//                             <g />
//                             <g />
//                             <path
//                               style={{
//                                 fill: "rgb(237, 237, 237)",
//                                 strokeWidth: 1,
//                               }}
//                               d="M 576.076 302.21 L 140.876 302.21 C 119.756 302.21 102.476 284.93 102.476 263.81 L 102.476 7.81 C 102.476 -13.31 119.756 -30.59 140.876 -30.59 L 576.076 -30.59 C 597.196 -30.59 614.476 -13.31 614.476 7.81 L 614.476 263.81 C 614.476 284.93 597.196 302.21 576.076 302.21 Z"
//                             />
//                             <path
//                               style={{
//                                 fill: "rgb(216, 216, 218)",
//                                 strokeWidth: 1,
//                               }}
//                               d="M 576.076 -30.59 L 512.076 -30.59 C 533.196 -30.59 550.476 -13.31 550.476 7.81 L 550.476 263.81 C 550.476 284.93 533.196 302.21 512.076 302.21 L 576.076 302.21 C 597.196 302.21 614.476 284.93 614.476 263.81 L 614.476 7.81 C 614.476 -13.31 597.196 -30.59 576.076 -30.59 Z"
//                             />
//                             <rect
//                               y="46.21"
//                               style={{
//                                 fill: "rgb(136, 136, 143)",
//                                 strokeWidth: 1,
//                               }}
//                               width="512"
//                               height="76.8"
//                               x="102.476"
//                             />
//                             <rect
//                               x="550.476"
//                               y="46.21"
//                               style={{
//                                 fill: "rgb(121, 119, 129)",
//                                 strokeWidth: 1,
//                               }}
//                               width="64"
//                               height="76.8"
//                             />
//                             <circle
//                               style={{
//                                 fill: "rgb(224, 113, 136)",
//                                 strokeWidth: 1,
//                               }}
//                               cx="192.076"
//                               cy="212.61"
//                               r="38.4"
//                             />
//                             <circle
//                               style={{
//                                 fill: "rgb(248, 233, 155)",
//                                 strokeWidth: 1,
//                               }}
//                               cx="243.276"
//                               cy="212.61"
//                               r="38.4"
//                             />
//                             <path
//                               style={{
//                                 fill: "rgb(136, 136, 143)",
//                                 strokeWidth: 1,
//                               }}
//                               d="M 540.876 196.61 L 336.076 196.61 C 330.774 196.61 326.476 192.313 326.476 187.01 C 326.476 181.707 330.774 177.41 336.076 177.41 L 540.876 177.41 C 546.179 177.41 550.476 181.707 550.476 187.01 C 550.476 192.313 546.179 196.61 540.876 196.61 Z"
//                             />
//                             <path
//                               style={{
//                                 fill: "rgb(136, 136, 143)",
//                                 strokeWidth: 1,
//                               }}
//                               d="M 515.276 235.01 L 336.076 235.01 C 330.774 235.01 326.476 230.713 326.476 225.41 C 326.476 220.107 330.774 215.81 336.076 215.81 L 515.276 215.81 C 520.579 215.81 524.876 220.107 524.876 225.41 C 524.876 230.713 520.579 235.01 515.276 235.01 Z"
//                             />
//                             <text
//                               style={{
//                                 fill: "rgb(120, 226, 15)",
//                                 fontFamily: "Arial, sans-serif",
//                                 fontSize: "28px",
//                                 whiteSpace: "pre",
//                               }}
//                               x="185.029"
//                               y="90.027"
//                             >
//                               DEBIT CARD
//                             </text>
//                           </g>
//                         </g>
//                       </g>
//                     </g>
//                   </svg>

//                   {loading ? "Processing…" : "Pay with Debit Card"}
//                 </button>
//               </form> */}
